import moment from 'moment'
import _ from 'lodash'
import { isEmpty, get } from 'lodash/fp'
import { isInvestmentInputsValid } from 'quick-quote/product-investment/journey-control-selectors'

import { isQQFlow } from 'quick-quote/portal/selectors'
import { isAllowReviseQQMenu } from 'identity/selectors'
import { createSelector } from 'reselect'
import { getSelectedBasicCategory, getSelectedDisplayProductCode } from 'quick-quote/product-selection/selectors'
import VALUES from 'core/data-model/constants/values'
import {
  getBirthdate,
  getBirthdateErrors,
  getInsuredOccupationErrors,
  getInsuredOtherOccupationErrors,
  getIsSelectedOtherOccupation,
  getIsValidBirthdate,
  getPayerBirthdate,
  getPayerIsValidBirthdate,
  getPayerNeeded,
  getPayerOccupationErrors,
  getSelectedNatureOfDutyCode,
  getSelectedOtherNatureOfDutyCode,
  getSelectedPayerNatureOfDutyCode,
} from 'quick-quote/insured-information/selectors'
import { isCompletedMarketConducts } from 'quick-quote/fna/market-conducts/selectors'
import { isCompletedNeedGap } from 'quick-quote/fna/need-and-gap-analysis/selectors'
import { isOnFnaPhase } from 'quick-quote/fna/selectors'
import { getToggles } from 'quick-quote/feature-toggles'

import { getUserAgentType, getUserDistributorGroup } from 'identity/selectors'
import { IsRealDevice } from 'e-submission/utils'

export const getApplicationStatus = (state) => state.status
export const getVersion = (state) => state.version
export const getEnableMaPage = (state) => state.enableMaPage
export const getEnableRetrieveCustomerPolicy = (state) => state.enableRetrieveCustomerPolicy
export const getAppVersion = () => {
  return process.env.APP_VERSION || ''
}
export const getAppName = () => {
  return process.env.APP_NAME || ''
}
export const getIsUserOnline = () => navigator.onLine
export const getViewConfiguration = (state) => state.viewConfiguration

export const getAllowedAgentTypes = (state) => state.allowedAgentTypes
export const isSelectedRemoteSelling = (state) => state.selectedRemoteSelling
export const getIsAllowRemoteSelling = createSelector(
  getSelectedBasicCategory,
  getUserAgentType,
  (productCategory, agentType) => {
    return getToggles().ENABLE_INVESTMENT_PRODUCT_REMOTESELLING_WITHOUT_BANCA
      ? agentType !== 'LB'
      : productCategory !== VALUES.INVESTMENT && agentType !== 'LB'
  }
)

const isOccupationInvalid = (natureOfDuty, occpationErrors) => isEmpty(natureOfDuty) || !isEmpty(occpationErrors)
const isOtherOccupationInvalid = (selected, natureOfDuty, occpationErrors) =>
  selected && isOccupationInvalid(natureOfDuty, occpationErrors)
const isInvalidBirthdate = (birthdate, isValidbirthdate) => isEmpty(birthdate) || !isValidbirthdate
const isSomeOccupationInvalid = (isOccupationInvalid, isOtherOccupationInvalid) =>
  isOccupationInvalid || isOtherOccupationInvalid
const isBirthdateOrOccupationInvalid = (isBirthdateInvalid, hasSomeOccupationInvalid) =>
  isBirthdateInvalid || hasSomeOccupationInvalid

const isInsuredBirthdateInvalid = createSelector(getBirthdate, getIsValidBirthdate, isInvalidBirthdate)

const isPayerBirthdateInvalid = createSelector(getPayerBirthdate, getPayerIsValidBirthdate, isInvalidBirthdate)

const isInsuredOccupationInvalid = createSelector(
  getSelectedNatureOfDutyCode,
  getInsuredOccupationErrors,
  isOccupationInvalid
)

const isPayerOccupationInvalid = createSelector(
  getSelectedPayerNatureOfDutyCode,
  getPayerOccupationErrors,
  isOccupationInvalid
)

const isInsuredOtherOccupationInvalid = createSelector(
  getIsSelectedOtherOccupation,
  getSelectedOtherNatureOfDutyCode,
  getInsuredOtherOccupationErrors,
  isOtherOccupationInvalid
)

const isPayerOtherOccupationInvalid = (state) => {
  // TODO: Waiting for requirement
  // const selected = state.insured.payer.otherOccupation.isSelected
  // const natureOfDuty = state.insured.payer.otherOccupation.natureOfDuty.code
  // const occpationErrors = state.insured.payer.otherOccupation.errors
  // return isOtherOccupationInvalid(selected, natureOfDuty, occpationErrors)
  return false
}

const isInsuredSomeOccupationInvalid = createSelector(
  isInsuredOccupationInvalid,
  isInsuredOtherOccupationInvalid,
  isSomeOccupationInvalid
)

const isPayerSomeOccupationInvalid = createSelector(
  isPayerOccupationInvalid,
  isPayerOtherOccupationInvalid,
  isSomeOccupationInvalid
)

const isInsuredBirthdateOrOccupationInvalid = createSelector(
  isInsuredBirthdateInvalid,
  isInsuredSomeOccupationInvalid,
  isBirthdateOrOccupationInvalid
)

const isPayerBirthdateOrOccupationInvalid = createSelector(
  isPayerBirthdateInvalid,
  isPayerSomeOccupationInvalid,
  isBirthdateOrOccupationInvalid
)

export const validationEnteringESubRules = (distributorGroup, rules, displayProductCode, currentDate) => {
  const validatedRule = _.get(rules, `${displayProductCode}.allowAgentRule`)
  const shouldValidateRule = validatedRule && validatedRule.agentTypes[0][distributorGroup] ? true : false
  if (shouldValidateRule) {
    const startDate = moment(
      validatedRule.agentTypes[0][distributorGroup].startDate,
      VALUES.DATE_FORMAT,
      VALUES.TIME_ZONE
    )
    const endDate = moment(validatedRule.agentTypes[0][distributorGroup].endDate, VALUES.DATE_FORMAT, VALUES.TIME_ZONE)
    const isValid = currentDate.isSameOrAfter(startDate) && currentDate.isSameOrBefore(endDate)
    return isValid
  } else {
    return false
  }
}

const _validateAgentEnteringEsub = createSelector(
  getUserDistributorGroup,
  getSelectedDisplayProductCode,
  (distributorGroup, displayProductCode) => {
    const rules = VALUES.VALIDATED_ENTERING_ESUB_RULES
    const validatedRule = _.get(rules, `${displayProductCode}.allowAgentRule`)
    const currentDate = moment(new Date()).tz(VALUES.TIME_ZONE)
    const _isEnteringEsub = (validatedRule) =>
      validatedRule ? validationEnteringESubRules(distributorGroup, rules, displayProductCode, currentDate) : true
    return _isEnteringEsub(validatedRule) ? [] : [validatedRule.errorMessage]
  }
)

export const getValidatedEnteringESubRules = createSelector(
  _validateAgentEnteringEsub,
  (validatedAgentEnteringEsub) => {
    return validatedAgentEnteringEsub
  }
)

export const isBlockedPdfGeneration = createSelector(
  getBirthdateErrors,
  getBirthdate,
  getValidatedEnteringESubRules,
  (birthErrors, birthValue, enteringErrors) => {
    return !_.isEmpty(birthErrors) || _.isEmpty(birthValue) || enteringErrors.length > 0
  }
)

export const isBlockedEnteringESubInvestment = createSelector(
  isInsuredBirthdateOrOccupationInvalid,
  getPayerNeeded,
  isPayerBirthdateInvalid,
  (isInsuredInvalid, isPayerNeed, isPayerInvalid) => isInsuredInvalid || (isPayerNeed && isPayerInvalid)
)

export const isBlockedEnteringESubWholeLife = createSelector(
  isInsuredBirthdateOrOccupationInvalid,
  getPayerNeeded,
  isPayerBirthdateOrOccupationInvalid,
  (isInsuredInvalid, isPayerNeed, isPayerInvalid) => isInsuredInvalid || (isPayerNeed && isPayerInvalid)
)

export const _isDisabledESubButton = (
  isBlockedEnteringESubWholeLife,
  isBlockedEnteringESubInvestment,
  canProceedToInvestmentBenefitIllustration,
  productCategory
) => {
  if (productCategory === VALUES.INVESTMENT) {
    return isBlockedEnteringESubInvestment || !canProceedToInvestmentBenefitIllustration
  }
  return isBlockedEnteringESubWholeLife
}

export const isBlockedEnteringESub = createSelector(
  isBlockedEnteringESubWholeLife,
  isBlockedEnteringESubInvestment,
  isInvestmentInputsValid,
  getSelectedBasicCategory,
  _isDisabledESubButton
)

export const isProcessing = (state) => state.processing

export const getAppState = (state) => state

export const getFnaConfiguration = (state) => state.fnaConfiguration

export const isCompletedFnaPhase = createSelector(
  isCompletedNeedGap,
  isCompletedMarketConducts,
  (completedNeedGap, completedMarketConducts) => {
    return completedNeedGap && completedMarketConducts
  }
)

export const isNeedToCompleteFNA = createSelector(
  isOnFnaPhase,
  isCompletedFnaPhase,
  (onFnaPhase, completedFnaPhase) => getToggles().ENABLE_FNA_FLOW && !completedFnaPhase
)

export const isQuickQuotePath = () => {
  let currentPath
  if (IsRealDevice) {
    currentPath = window.location.hash
  } else {
    currentPath = window.location.pathname
  }
  const quickQuotePath = '/advisors/quote/quick'
  return currentPath.includes(quickQuotePath)
}

export const isReviseQQFlow = createSelector(
  isQQFlow,
  isAllowReviseQQMenu,
  (isQQFlow, isAllowReviseQQ) => isQQFlow && isAllowReviseQQ
)
